import { API, Auth, graphqlOperation } from 'aws-amplify';

const PUBLIC_VAPID_KEY = process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY;
const LOCAL_STORAGE_KEY = 'ac:webpush:last-subscription';
const APP_NAME = process.env.REACT_APP_APPLICATION_NAME || 'AdvisoryCloud';

const saveDeviceSubscriptionMutation = `
  mutation saveDeviceSubscription(
    $endpoint: String!
    $p256dh: String!
    $auth: String!
    $expirationTime: String
    $accountId: String!
    $accountType: String!
    $userId: String!
  ) {
    saveDeviceSubscription(
      endpoint: $endpoint
      p256dh: $p256dh
      auth: $auth
      expirationTime: $expirationTime
      accountId: $accountId
      accountType: $accountType
      userId: $userId
    ) {
      endpoint
    }
  }
`;

const deleteDeviceSubscriptionMutation = `
  mutation deleteDeviceSubscription($endpoint: String!) {
    deleteDeviceSubscription(endpoint: $endpoint)
  }
`;

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const getAuthToken = async () => {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (error) {
    console.warn('No auth token available for push subscription', error);
    return null;
  }
};

const getServiceWorkerRegistration = async () => {
  if (!('serviceWorker' in navigator)) return null;

  const existingRegistration = await navigator.serviceWorker.getRegistration();
  if (existingRegistration) return existingRegistration;
  try {
    const registration = await navigator.serviceWorker.ready;
    return registration;
  } catch (error) {
    console.error(
      `${APP_NAME}: Error waiting for service worker to be ready`,
      error
    );
    console.warn('Service worker not ready for push', error);
    return null;
  }
};

const shouldSkipSubscription = (subscription, meta) => {
  try {
    const saved = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY));
    if (!saved) return false;
    return (
      saved.endpoint === subscription?.endpoint &&
      saved.accountId === meta.accountId &&
      saved.userId === meta.userId &&
      saved.accountType === meta.accountType
    );
  } catch (_error) {
    return false;
  }
};

const persistSubscriptionLocally = (subscription, meta) => {
  try {
    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify({
        endpoint: subscription?.endpoint,
        accountId: meta.accountId,
        userId: meta.userId,
        accountType: meta.accountType,
      })
    );
  } catch (_error) {
    // Fail silently; push will still work even if we cannot cache locally.
  }
};

const getPersistedSubscription = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY));
    return saved || null;
  } catch (_error) {
    return null;
  }
};

const sendSubscriptionToBackend = async (subscription, meta) => {
  const toBase64 = (buffer) => {
    if (!buffer) return '';
    return btoa(String.fromCharCode.apply(null, new Uint8Array(buffer)));
  };

  const payload = {
    endpoint: subscription?.endpoint,
    p256dh: toBase64(subscription?.getKey && subscription.getKey('p256dh')),
    auth: toBase64(subscription?.getKey && subscription.getKey('auth')),
    expirationTime: subscription?.expirationTime
      ? String(subscription.expirationTime)
      : null,
    accountId: meta.accountId,
    accountType: meta.accountType,
    userId: meta.userId,
  };

  if (!payload.endpoint || !payload.p256dh || !payload.auth) {
    console.warn('Missing subscription keys; skipping push subscription save');
    return;
  }

  // Ensure we still have a valid session before attempting the mutation.
  const token = await getAuthToken();
  if (!token) return;

  await API.graphql(graphqlOperation(saveDeviceSubscriptionMutation, payload));
};

export const ensurePushSubscription = async ({
  accountId,
  accountType,
  userId,
  userInitiated = false,
} = {}) => {
  const isSafari =
    typeof window !== 'undefined' &&
    /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);

  if (typeof window === 'undefined') {
    console.warn('Push: window not available');
    return null;
  }
  if (!('Notification' in window)) {
    console.warn('Push: Notification API not available');
    return null;
  }

  if (!('serviceWorker' in navigator)) {
    console.warn('Push: service workers not supported');
    return null;
  }

  if (!PUBLIC_VAPID_KEY) {
    console.warn('Push: PUBLIC_VAPID_KEY not configured');
    return null;
  }

  if (isSafari && Notification.permission === 'default' && !userInitiated) {
    console.warn(
      'Safari requires a user gesture to show the native prompt; ' +
        'call ensurePushSubscription with userInitiated=true from a click handler.'
    );
    return null;
  }

  try {
    const swReady = await navigator.serviceWorker.ready;
  } catch (error) {
    console.error('Service worker is not active or ready', error);
    return null;
  }

  const registration = await getServiceWorkerRegistration();
  if (!registration) return null;

  if (!registration.pushManager) {
    console.warn('Push: registration.pushManager is not available');
    return null;
  }

  if (Notification.permission === 'denied') {
    console.warn('Push notifications permission denied');
    return null;
  }

  const permission =
    Notification.permission === 'granted'
      ? 'granted'
      : await Notification.requestPermission();

  if (permission !== 'granted') {
    return null;
  }

  const existing = await registration.pushManager.getSubscription();
  let subscription = existing;

  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY),
      });
    } catch (error) {
      console.error('Error subscribing to push manager', error);
      return null;
    }
  }

  const meta = { accountId, accountType, userId };

  if (!shouldSkipSubscription(subscription, meta)) {
    try {
      await sendSubscriptionToBackend(subscription, meta);
      persistSubscriptionLocally(subscription, meta);
    } catch (error) {
      console.error('Error persisting push subscription', error);
    }
  }

  return subscription;
};

export const deletePushSubscription = async () => {
  try {
    const token = await getAuthToken();
    if (!token) return;

    const registration = await getServiceWorkerRegistration();
    const activeSubscription =
      await registration?.pushManager?.getSubscription();
    const persisted = getPersistedSubscription();
    const endpoint =
      activeSubscription?.endpoint || persisted?.endpoint || null;

    if (!endpoint) {
      return;
    }

    await API.graphql(
      graphqlOperation(deleteDeviceSubscriptionMutation, { endpoint })
    );

    try {
      await activeSubscription?.unsubscribe();
    } catch (_err) {
      // Best effort; ignore.
    }

    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch (_err) {
      // ignore
    }
  } catch (error) {
    console.warn('Push: error deleting device subscription', error);
  }
};

export const getNotificationPermissions = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};
